import { formatNumber, formatPercent } from '@angular/common';
import { CmpsInsightCoachingPerformanceData, ToolTipData } from '@cmps/ng-kit';
import { TranslateService } from '@ngx-translate/core';

import { bgGradient } from '../../common/pipe/bg-gradient';
import { CardPerformanceDataContainer as MetricItem } from '../../insight/models/card-performance-data-container.model';
import { epicDependencies } from '../../store';
import { ColourGroup } from '../models/colour-group.model';
import { TranslationKeyMap } from '../my-performance-card.i18n';

import { extractMetrics } from './extract-metrics.function';
export function processColourGroups(
    colourGroups: ColourGroup[],
): ColourGroup[] {
    const grayGroup: ColourGroup = {
        colour: '#bababa', // Gray color
        metrics: [],
    };

    const processedGroups = colourGroups.map((group) => {
        if (group.metrics == null || group.metrics.length === 0) {
            return group;
        }

        // Use reduce to separate metrics
        const [nullTargetMetrics, validMetrics] = group.metrics.reduce(
            ([nullTarget, valid], metric): [MetricItem[], MetricItem[]] => {
                if (
                    metric.target === null ||
                    metric.thresholdBands?.length === 0
                ) {
                    nullTarget.push(metric);
                } else {
                    valid.push(metric);
                }
                return [nullTarget, valid];
            },
            [[], []],
        );

        grayGroup.metrics?.push(...nullTargetMetrics);

        return {
            ...group,
            metrics: validMetrics,
        };
    });

    if (grayGroup.metrics != null && grayGroup.metrics.length > 0) {
        processedGroups.push(grayGroup);
    }

    return processedGroups;
}
function sortColourGroups(colourGroups: ColourGroup[]): ColourGroup[] {
    const colorOrder = [
        '#4b8a08', // Green
        '#000000', // Black
        '#ff9100', // Yellow
        '#df0101', // Red
        '#bababa', // Gray
        '#8a0886', // Gray
    ];

    return colourGroups.sort((a, b) => {
        const indexA = colorOrder.indexOf(a.colour.toLowerCase());
        const indexB = colorOrder.indexOf(b.colour.toLowerCase());
        return indexA - indexB;
    });
}

export function getProgressDataForPillar(
    colourGroups: ColourGroup[],
): CmpsInsightCoachingPerformanceData[] {
    if (epicDependencies == null || epicDependencies.injector == null) {
        return [];
    }

    const sortedColourGroups = sortColourGroups(colourGroups);
    const translateService =
        epicDependencies.injector.get<TranslateService>(TranslateService);
    const metrics = extractMetrics(sortedColourGroups);

    const createTooltipData = (metric: MetricItem): ToolTipData => ({
        title:
            metric.metricName != null
                ? metric.metricName
                : translateService.instant(TranslationKeyMap._9944_BLANK),
        number: formatNumber(metric.actual ?? 0, 'en-CA', '1.0-2'),
    });

    const createProgressBarData = (
        group: ColourGroup,
        metricsCount: number,
    ): CmpsInsightCoachingPerformanceData => {
        const tooltipData = [
            {
                title: translateService.instant(
                    TranslationKeyMap._7339_METRIC_NAME,
                ),
                number: translateService.instant(TranslationKeyMap._725_ACTUAL),
                className: 'cmps-tooltip-header',
            },
            ...(group.metrics?.map(createTooltipData) ?? []),
        ];
        const color =
            group.metrics?.some((m) => m.target == null) ?? false
                ? '#bababa'
                : group.colour;
        const metricsLength =
            group.metrics != null && group.metrics.length > 0
                ? group.metrics.length
                : 0;

        return {
            percent:
                metricsLength > 0
                    ? formatPercent(
                          metricsLength / metricsCount,
                          'en-CA',
                          '1.4',
                      )
                    : undefined,
            backgroundColor: color,
            backgroundColorGradient: bgGradient({ color }),
            metricsLength,
            enabled: true,
            tooltipData,
        };
    };

    return (
        sortedColourGroups?.map((group) =>
            createProgressBarData(group, metrics.length),
        ) ?? []
    );
}
